import { useState, type ReactNode } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import { ActionConfirmDialog } from '@/apps/latest/components/molecules/ActionConfirmDialog'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { NOTION_BUTTON_TEXT_SX } from '@/shared/styles/buttonStyles'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_TABULAR,
  FONT_WEIGHT,
  scaledFontSize,
} from '@/shared/styles/typography'

import {
  INTAKE_KEYS,
  type IntakeAmounts,
  type IntakeKey,
  type NutritionFood,
  type NutritionIntake,
  type NutritionIntakeDish,
  type NutritionIntakeFood,
} from './api'
import {
  useEditNutritionIntake,
  useNutritionFoods,
  useRecomputeNutritionIntake,
} from './useNutritionIntake'

const COLUMNS: Record<IntakeKey, string> = {
  ENERC_KCAL: 'kcal',
  'PROT-': 'たんぱく質',
  'FAT-': '脂質',
  'CHOCDF-': '炭水化物',
  NACL_EQ: '食塩',
}
// 人が直す g の上限 (back の MAX_GRAMS と同じ。桁違いの値は back が弾く)
const MAX_GRAMS = 5000
// 名前 │ g │ 5 成分 │ 消す
const GRID = 'minmax(0, 1fr) 72px repeat(5, 80px) 32px'

const format = (key: IntakeKey, v: number | null) =>
  v === null ? '-' : key === 'ENERC_KCAL' ? String(Math.round(v)) : v.toFixed(1)

// 打てる欄は常に白 (bg.default)。記録のブロックの中は格子と同じ 1 つの規則で、白 = 打てる・地 = 印字と計算の数字
// (design-book ADR-0083。ADR-0081 決定 5 の「地のまま・hover で浮く」は、打てる欄が地に溶けて見えなかった)。
// focus は格子と同じ humble なリング。前の回 (読むだけ) は欄を出さないので白も出ない。
const cellInputSx = {
  fontSize: scaledFontSize('bodySm'),
  borderRadius: `${RADIUS.sm}px`,
  bgcolor: colorTokens.bg.default,
  '&.Mui-focused': {
    boxShadow: colorTokens.focus.ringNeutral,
  },
} as const

const Row = ({
  name,
  grams,
  amounts,
  strong,
  indent = 0,
  action,
}: {
  name: ReactNode
  grams?: ReactNode
  amounts?: IntakeAmounts
  // 食事の行 (小計) と 1 日の合計だけ。太さは 2 段 (この 2 つ・それ以外) にする
  strong?: boolean
  indent?: number
  action?: ReactNode
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: GRID,
      alignItems: 'center',
      columnGap: 1,
      minHeight: 32,
      pl: indent,
      borderBottom: `1px solid ${colorTokens.border.subtle}`,
      fontSize: scaledFontSize('bodySm'),
      fontWeight: strong ? FONT_WEIGHT.semibold : FONT_WEIGHT.regular,
      color: colorTokens.fg.default,
      // 消す口は行に乗ったとき・行の中に focus があるときだけ見せる (常に並ぶと × の列が目立つ)
      '& [data-row-action]': { opacity: 0 },
      '&:hover [data-row-action], &:focus-within [data-row-action]': {
        opacity: 1,
      },
    }}
  >
    <Box sx={{ minWidth: 0 }}>{name}</Box>
    <Box sx={{ textAlign: 'right', ...FONT_TABULAR }}>{grams}</Box>
    {INTAKE_KEYS.map((k) => (
      <Box key={k} sx={{ textAlign: 'right', ...FONT_TABULAR }}>
        {amounts ? format(k, amounts[k]) : ''}
      </Box>
    ))}
    <Box>{action}</Box>
  </Box>
)

// 成分表から食品を選ぶ欄 (打つと似た名前の 15 件)。成分表に無い名前は入らない。選ぶと送る。字を消しても行は消さない
// (打ち直す途中で消えないように。消すのは行の × )。
const FoodPicker = ({
  value,
  placeholder,
  ariaLabel,
  onPick,
  disabled,
}: {
  value: NutritionFood | null
  placeholder?: string
  ariaLabel: string
  onPick: (food: NutritionFood) => void
  disabled?: boolean
}) => {
  const [input, setInput] = useState(value?.label ?? '')
  const { data: options = [], isFetching } = useNutritionFoods(
    input === value?.label ? '' : input
  )
  return (
    <Autocomplete<NutritionFood, false, true, false>
      size="small"
      disableClearable
      options={
        value
          ? [value, ...options.filter((o) => o.code !== value.code)]
          : options
      }
      value={value ?? undefined}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, picked) => {
        if (picked && picked.code !== value?.code) onPick(picked)
      }}
      // 選ばずに離れたら、今の食品の名前に戻す
      onBlur={() => setInput(value?.label ?? '')}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      filterOptions={(x) => x}
      loading={isFetching}
      loadingText="探しています…"
      disabled={disabled}
      noOptionsText={
        input ? '成分表に似た名前がありません' : '名前を打つと候補が出ます'
      }
      renderInput={(params) => (
        <InputBase
          ref={params.InputProps.ref}
          inputProps={{
            ...params.inputProps,
            'aria-label': ariaLabel,
          }}
          placeholder={placeholder}
          sx={{
            width: '100%',
            px: 0.5,
            ...cellInputSx,
            '& input::placeholder': {
              color: colorTokens.fg.muted,
              opacity: 1,
            },
          }}
        />
      )}
    />
  )
}

// 数字の欄 (g)。欄を離れた時点で、変わっていれば送る。送れなかったとき・上限で丸めたときは、欄を
// 保存されている値 (または送った値) に戻す (打った字のまま残すと、保存されていない数字が見え続ける)。
const NumberInput = ({
  value,
  label,
  onCommit,
  disabled,
  max,
}: {
  value: number
  label: string
  onCommit: (v: number) => Promise<unknown>
  disabled?: boolean
  max?: number
}) => {
  const [text, setText] = useState(String(value))
  // 応答で値が替わったら欄も替える (props から決まる状態は描画の中で揃える)
  const [shown, setShown] = useState(value)
  if (shown !== value) {
    setShown(value)
    setText(String(value))
  }
  return (
    <InputBase
      value={text}
      disabled={disabled}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const n = Number(text)
        if (text.trim() === '' || !Number.isFinite(n) || n < 0 || n === value) {
          setText(String(value))
          return
        }
        const sent = max === undefined ? n : Math.min(n, max)
        setText(String(sent))
        onCommit(sent).catch(() => setText(String(value)))
      }}
      inputProps={{ 'aria-label': label, inputMode: 'decimal' }}
      sx={{
        width: 56,
        '& input': { textAlign: 'right', px: 0.5, ...FONT_TABULAR },
        ...cellInputSx,
      }}
    />
  )
}

interface Props {
  eventId: string
  intake: NutritionIntake | null | undefined
}

// 完了画面の「栄養価」のタブの中身 (design-book ADR-0078・0081)。回に乗る 4 つ目。
// 頭に 1 日の合計 (食事の並びとは余白で分ける。ADR-0094)、食事 (小計) → 料理 → 材料 (食品・g・5 成分)。1 日分の食事なので、材料の行を足せば小計になる
// (yakureki-back log/274 決定 1)。
// 数字は back が成分表から出す。人が直すのは 食品 (成分表から選ぶ)・g で、料理ごとの最後の空行が足す場所、
// 行の × で消す。back が報告書と一緒に計算して着地させ、食事の欄を後から直したときだけ「計算し直す」を出す。
// 人が直した計算で押したときだけ 1 行の確認を挟む (直した食品と g が置き換わる。人の手を黙って捨てない。
// yakureki-back log/272 決定 8 の B2)。どの回も同じ形で直せる (ADR-0086 決定 6)。
export const NutritionIntakeSection = ({ eventId, intake }: Props) => {
  const recompute = useRecomputeNutritionIntake(eventId)
  const edit = useEditNutritionIntake(eventId)
  const busy = recompute.isPending || edit.isPending
  const [confirm, setConfirm] = useState(false)

  const action =
    intake === null || intake?.stale ? (
      <Button
        variant="text"
        size="small"
        sx={NOTION_BUTTON_TEXT_SX}
        onClick={() => (intake?.edited ? setConfirm(true) : recompute.mutate())}
        disabled={busy}
        startIcon={
          recompute.isPending ? <CircularProgress size={12} /> : undefined
        }
      >
        {intake === null ? '食事の欄から計算する' : '食事の欄から計算し直す'}
      </Button>
    ) : null

  const removeButton = (f: NutritionIntakeFood) => (
    <IconButton
      data-row-action
      size="small"
      aria-label={`${f.label} を消す`}
      disabled={busy}
      onClick={() =>
        void edit
          .mutateAsync({ op: 'remove', path: f.path })
          .catch(() => undefined)
      }
      sx={{ color: colorTokens.fg.muted }}
    >
      <CloseIcon sx={{ fontSize: 16 }} />
    </IconButton>
  )

  const foodRow = (dish: NutritionIntakeDish, f: NutritionIntakeFood) => (
    <Row
      key={f.path.join('-')}
      indent={2}
      name={
        <FoodPicker
          value={f.code ? { code: f.code, label: f.label } : null}
          // 当てられなかった材料は、生成の書いた名前を薄く見せる (選ぶと入る)
          placeholder={f.code ? undefined : f.name}
          ariaLabel={`${dish.dish} の食品`}
          disabled={busy}
          onPick={(food) =>
            edit.mutate({
              op: 'food',
              path: f.path,
              code: food.code,
              grams: f.grams,
            })
          }
        />
      }
      grams={
        <NumberInput
          value={f.grams}
          label={`${f.label} の g`}
          disabled={busy || !f.code}
          max={MAX_GRAMS}
          onCommit={(grams) =>
            edit.mutateAsync({
              op: 'food',
              path: f.path,
              code: f.code,
              grams,
            })
          }
        />
      }
      amounts={f.amounts}
      action={removeButton(f)}
    />
  )

  return (
    <Box>
      {action && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          {action}
        </Box>
      )}
      {intake && intake.meals.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          食事の欄に食べ物がありません。
        </Typography>
      )}
      {intake && intake.meals.length > 0 && (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID,
              columnGap: 1,
              minHeight: 28,
              alignItems: 'end',
              borderBottom: `1px solid ${colorTokens.border.subtle}`,
              fontSize: scaledFontSize('tag'),
              color: colorTokens.fg.muted,
            }}
          >
            <Box />
            <Box sx={{ textAlign: 'right' }}>g</Box>
            {INTAKE_KEYS.map((k) => (
              <Box key={k} sx={{ textAlign: 'right' }}>
                {COLUMNS[k]}
              </Box>
            ))}
            <Box />
          </Box>
          {/* 1 日の合計は表の頭。最後に置くと間食の続きの 1 行に見える (先方の改善依頼 2026-10-28・design-book ADR-0094)。
              食事の並びとは余白で分ける (線は足さない) */}
          <Row strong name="1 日の合計" amounts={intake.total} />
          {intake.unmatched > 0 && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, color: colorTokens.fg.muted }}
            >
              成分表の食品に当てられなかったもの {intake.unmatched}{' '}
              つは合計に入っていません。食品の欄で選ぶと入ります。
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            {intake.meals.map((meal) => (
              <Box key={meal.meal}>
                <Row strong name={meal.meal} amounts={meal.subtotal} />
                {meal.dishes.map((dish) => (
                  <Box key={dish.path.join('-')}>
                    <Row indent={1} name={dish.dish} />
                    {dish.foods.map((f) => foodRow(dish, f))}
                    {
                      <Row
                        indent={2}
                        name={
                          <FoodPicker
                            // 足すたびに空の欄に戻す
                            key={`add-${dish.foods.length}`}
                            value={null}
                            placeholder="食品を足す"
                            ariaLabel={`${dish.dish} に食品を足す`}
                            disabled={busy}
                            onPick={(food) =>
                              edit.mutate({
                                op: 'add',
                                path: dish.path,
                                code: food.code,
                                grams: 0,
                              })
                            }
                          />
                        }
                      />
                    }
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, color: colorTokens.fg.faint }}
          >
            出典: {intake.source}
          </Typography>
        </Box>
      )}
      <ActionConfirmDialog
        open={confirm}
        title="食事の欄から計算し直す"
        body="直した食品と g は、食事の欄から計算し直した値に置き換わります。"
        actionLabel="計算し直す"
        onClose={() => setConfirm(false)}
        onConfirm={() => recompute.mutate()}
      />
    </Box>
  )
}
